import { AnimatePresence, motion } from 'framer-motion';
import { pullRefreshIgnore } from '@/hooks/usePullToRefresh';

/**
 * Reusable bottom-sheet confirmation dialog (wireframe 4c).
 * Danger-styled by default (red confirm). Tapping the backdrop cancels.
 */
export default function ConfirmSheet({
  open,
  title,
  cancelLabel,
  confirmLabel,
  onConfirm,
  onClose,
  loading = false,
}: {
  open: boolean;
  title: string;
  cancelLabel: string;
  confirmLabel: string;
  onConfirm: () => void;
  onClose: () => void;
  loading?: boolean;
}) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="tw:fixed tw:inset-0 tw:z-50 tw:flex tw:items-end tw:bg-black/60"
          onClick={onClose}
          data-testid="confirm-sheet"
          {...pullRefreshIgnore}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="tw:w-full tw:rounded-t-3xl tw:bg-dark-1 tw:p-6 tw:text-center"
            style={{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}
            onClick={(e) => e.stopPropagation()}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
          >
            <div className="tw:mx-auto tw:mb-5 tw:h-1 tw:w-10 tw:rounded-full tw:bg-grey-4/50" />

            <h2 className="tw:mb-6 tw:text-[17px] tw:font-semibold tw:text-white">{title}</h2>

            <div className="tw:flex tw:flex-col tw:gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                data-testid="confirm-sheet-cancel"
                className="tw:h-13 tw:w-full tw:rounded-2xl tw:border tw:border-error tw:py-3.5 tw:text-base tw:font-semibold tw:text-error tw:transition-opacity tw:active:opacity-70 tw:disabled:opacity-50"
              >
                {cancelLabel}
              </button>
              <button
                type="button"
                onClick={onConfirm}
                disabled={loading}
                data-testid="confirm-sheet-confirm"
                className="tw:flex tw:h-13 tw:w-full tw:items-center tw:justify-center tw:rounded-2xl tw:bg-error tw:py-3.5 tw:text-base tw:font-semibold tw:text-white tw:transition-opacity tw:active:opacity-80 tw:disabled:opacity-60"
              >
                {loading ? (
                  <span className="tw:size-5 tw:animate-spin tw:rounded-full tw:border-2 tw:border-white/30 tw:border-t-white" />
                ) : (
                  confirmLabel
                )}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
